import React, { useContext } from 'react';
import { MbContext, REVIEW_LABEL, rm, cpsClass, muted, primary } from './mbApi';
import { usePicName } from './HandoffControl';
import { AssetPreview } from './AssetPreview';

export const DATA_STATE = {
  tiada_ujian: ['Tiada ujian', muted], belum_padan: ['Belum padan Ad ID', 'text-amber-400'],
  tiada_data: ['Tiada data Meta', 'text-amber-400'], separa: ['Data separa', 'text-amber-400'], lengkap: ['Lengkap', 'text-emerald-400'],
};

const spendOf = (r) => {
  if (!r.currencies.length) return '—';
  if (!r.currencies.some((c) => c !== 'MYR')) return rm(r.amount_spent_myr);
  return r.currencies.includes('MYR') ? `${rm(r.amount_spent_myr)} (MYR sahaja)` : `Bukan MYR (${r.currencies.join(', ')})`;
};

const joinsOf = (r, k) => (r.tests ? r[k] : '—');
const Cps = ({ v }) => <span className={cpsClass(v) || primary}>{rm(v)}</span>;

const Coverage = ({ r }) => {
  const [label, cls] = DATA_STATE[r.data_state] || [r.data_state, muted];
  return (
    <span className="block">
      <span className={cls}>{label}</span>
      <span className={`block text-xs ${muted}`}>{r.ads_with_data}/{r.ads} iklan ada data · {r.days} hari</span>
    </span>
  );
};

const Status = ({ r }) => (
  <span className="block text-xs space-y-0.5">
    <span className={`block ${primary}`}>{REVIEW_LABEL[r.review_status] || r.review_status}{r.archived ? ' · Arkib' : ''}</span>
    <span className={`block ${muted}`}>CRM {r.running_crm ? '✓' : '✗'} · Meta {r.active_meta ? '✓' : '✗'} · Delivery {r.delivering ? '✓' : '✗'}</span>
    <span className={`block ${r.sample_ok ? 'text-emerald-400' : 'text-amber-400'}`} title={r.sample_why.join('; ')}>
      {r.sample_ok ? 'Sampel cukup' : 'Belum cukup data'}
    </span>
  </span>
);

const useOpen = () => {
  const { openTask } = useContext(MbContext);
  return (r) => openTask(r.workflow_id, r.tests ? 'ujian' : 'bahan', r.creative_id);
};
const onKey = (fn) => (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } };

const Row = ({ r, open, picName }) => (
  <tr role="button" tabIndex={0} onClick={() => open(r)} onKeyDown={onKey(() => open(r))} aria-label={`Buka ${r.code} ${r.version_label}`}
    className="border-t border-[hsl(var(--border))] align-top cursor-pointer hover:bg-white/[0.03]" data-testid={`mb-compare-row-${r.creative_id}`}>
    <td className="py-2 pr-3"><AssetPreview c={{ id: `cmp-${r.creative_id}`, asset_link: r.asset_link, version_label: r.version_label }} small /></td>
    <td className="py-2 pr-3"><span className={`block font-semibold ${primary}`}>{r.version_label}</span><span className="font-mono text-xs text-[#F77F00]">{r.code}</span></td>
    <td className="py-2 pr-3 max-w-[220px]"><span className={`block ${primary} break-words`}>{r.angle_tag || r.angle || '—'}</span><span className={`block text-xs ${muted} break-words`}>{r.hook}</span></td>
    <td className="py-2 pr-3">{picName(r.pic_id)}</td>
    <td className="py-2 pr-3 whitespace-nowrap">{spendOf(r)}</td>
    <td className="py-2 pr-3">{joinsOf(r, 'joins_first')}</td>
    <td className="py-2 pr-3">{joinsOf(r, 'joins')}</td>
    <td className="py-2 pr-3"><Cps v={r.cps_first_myr} /></td>
    <td className="py-2 pr-3"><Cps v={r.cps_myr} /></td>
    <td className="py-2 pr-3"><Coverage r={r} /></td>
    <td className="py-2"><Status r={r} /></td>
  </tr>
);

const Card = ({ r, open, picName }) => (
  <div role="button" tabIndex={0} onClick={() => open(r)} onKeyDown={onKey(() => open(r))} aria-label={`Buka ${r.code} ${r.version_label}`}
    className="rounded-lg border border-[hsl(var(--border))] p-3 space-y-2 cursor-pointer hover:bg-white/[0.03]" data-testid={`mb-compare-card-${r.creative_id}`}>
    <div className="flex gap-3">
      <AssetPreview c={{ id: `cmpc-${r.creative_id}`, asset_link: r.asset_link, version_label: r.version_label }} small />
      <div className="min-w-0">
        <p className={`text-sm font-semibold ${primary} break-words`}>{r.version_label} <span className="font-mono text-xs text-[#F77F00]">{r.code}</span></p>
        <p className={`text-xs ${muted} break-words`}>{r.angle_tag || r.angle || '—'} · {picName(r.pic_id)}</p>
      </div>
    </div>
    <div className={`grid grid-cols-2 gap-x-3 gap-y-1 text-sm ${muted}`}>
      <span>Spend: <span className={primary}>{spendOf(r)}</span></span>
      <span>Join unik / semua: <span className={primary}>{joinsOf(r, 'joins_first')} / {joinsOf(r, 'joins')}</span></span>
      <span>CPS unik: <Cps v={r.cps_first_myr} /></span>
      <span>CPS semua: <Cps v={r.cps_myr} /></span>
    </div>
    <div className="grid grid-cols-2 gap-3 text-sm"><Coverage r={r} /><Status r={r} /></div>
  </div>
);

const HEAD = ['Bahan', 'Versi / kod', 'Angle / hook', 'PIC', 'Spend', 'Join unik', 'Semua join', 'CPS unik', 'CPS semua', 'Coverage', 'Status'];

export const CompareRows = ({ rows }) => {
  const open = useOpen();
  const picName = usePicName();
  return (
    <>
      <div className="hidden lg:block overflow-x-auto" data-testid="mb-compare-table">
        <table className={`w-full text-sm ${muted}`}>
          <thead><tr className="text-left text-xs uppercase tracking-wide">{HEAD.map((h) => <th key={h} className="pb-2 pr-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <Row key={r.creative_id} r={r} open={open} picName={picName} />)}</tbody>
        </table>
      </div>
      <div className="lg:hidden space-y-2" data-testid="mb-compare-cards">
        {rows.map((r) => <Card key={r.creative_id} r={r} open={open} picName={picName} />)}
      </div>
    </>
  );
};
